import type { ExclusionReason } from 'pages/StudyList/StudyListItem/ExclusionReason/ExclusionReason';
import { getCsrfToken } from 'query';
import { getReviewId } from 'query/review';
import {
  EXCLUSION_REASONS,
  REVIEW_SETUP_EXCLUSION_REASONS,
  REVIEW_SETUP_DELETE_EXCLUSION_REASON,
  REVIEW_SETUP_EXCLUSION_REASON_POSITIONS,
  tokenizeRoute,
} from 'query/routes';

export type ExclusionReasonWithPosition = {
  id: number;
  reason: string;
  position: number | null;
};

export type ExclusionReasonsResponse = {
  exclusion_reasons: ExclusionReasonWithPosition[];
  default_names: string[];
};

export const getExclusionReasons = async (): Promise<ExclusionReasonsResponse> => {
  const reviewId = getReviewId();
  if (!reviewId)
    throw new Error("Couldn't get Review ID. Is this part of a review?");

  const url = tokenizeRoute(EXCLUSION_REASONS, {
    review_id: reviewId,
  });

  return fetch(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
  }).then((res) => res.json());
};

export const deleteExclusionReason = async ({
  reason,
  onApiError,
}: {
  reason: ExclusionReason;
  onApiError: (errors: string[] | null) => void;
}) => {
  const reviewId = getReviewId();
  if (!reviewId) {
    onApiError(['Could not determine review ID']);
    return;
  }

  if (!reason) {
    onApiError(['Please provide a reason']);
    return;
  }

  const url = tokenizeRoute(REVIEW_SETUP_DELETE_EXCLUSION_REASON, {
    review_id: reviewId,
    exclusion_reason_id: reason.id,
  });

  return await fetch(url, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: { 'X-CSRF-Token': getCsrfToken() },
  });
};

export const reorderExclusionReasons = async ({
  positions,
  onApiError,
}: {
  positions: number[];
  onApiError: (errors: string[] | null) => void;
}) => {
  const reviewId = getReviewId();
  if (!reviewId) {
    onApiError(['Could not determine review ID']);
    return;
  }

  const url = tokenizeRoute(REVIEW_SETUP_EXCLUSION_REASON_POSITIONS, {
    review_id: reviewId,
  });

  return await fetch(url, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
    body: JSON.stringify({ positions }),
  });
};

export const createExclusionReason = async ({
  reason,
  onApiError,
}: {
  reason: ExclusionReason['reason'];
  onApiError: (errors: string[] | null) => void;
}) => {
  const reviewId = getReviewId();
  if (!reviewId) {
    onApiError(['Could not determine review ID']);
    return;
  }

  if (!reason) {
    onApiError(['Please provide a reason']);
    return;
  }

  const url = tokenizeRoute(REVIEW_SETUP_EXCLUSION_REASONS, {
    review_id: reviewId,
  });

  return await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
    body: JSON.stringify({
      exclusion_reason: {
        reason: reason,
      },
    }),
  });
};
